import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Modal, ModalSubTitle, useAssessmentColumns } from '../comps';
import { Poppins, Spacer, TableSkeleton } from 'src/common';
import { queryCache } from 'react-query';
import Button from 'src/components/form/Button';
import { QKeys } from 'src/api/types';
import { Guard } from '../Guard';
import { useScenarioColumns } from './useScenarioColumns';
import GenericTable from 'src/components/GenericTable';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { TableIds } from 'src/api/types/misc';
import { Back } from 'src/assets/dynamicSvg';
import { useAssessmentsQuery, useIncidentsQuery, useLinkIncidentToScenariosMutation } from 'src/graphql/_generated';
import { isQueryLoading } from 'src/graphql/client';
import { useCache } from 'src/graphql';

const StyledModal = styled(Modal)`
  width: 1350px;
  min-height: 200px;
  padding: 0 !important;
  max-height: calc(100% - 40px);
  overflow-y: auto;

  .back {
    display: flex;
    align-items: center;
    cursor: pointer;

    &__text {
      font-weight: 600;
      color: ${({ theme }) => theme.colors.cflowerBlue};
      margin-left: 13px;
    }
  }

  .error {
    color: ${({ theme }) => theme.colors.error};
    font-size: 14px;
    text-align: center;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
  }
`;

interface LinkScenariosToIncidentModalProps {
  onClose: () => void;
  incidentId: string;
  subTitle?: {
    title: string;
    value: string;
  };
}

export const LinkScenariosToIncidentModal: React.FC<LinkScenariosToIncidentModalProps> = ({
  onClose,
  incidentId,
  subTitle,
}) => {
  const [err, setErr] = useState('');
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const [incidentScenarios, setIncidentScenarios] = useState<string[]>([]);
  const { refetchQuery } = useCache();

  const { data, loading: loadingIncidents, networkStatus: networkStatusIncidents } = useIncidentsQuery();
  const isIncidentsLoading = isQueryLoading({ loading: loadingIncidents, data, networkStatus: networkStatusIncidents });
  const incidentScenariosData = data?.incidents || [];

  const {
    data: { assessments = [] } = {},
    loading: loadingAssessments,
    networkStatus: networkStatusAssessments,
  } = useAssessmentsQuery();
  const isAssessmentsLoading = isQueryLoading({
    loading: loadingAssessments,
    data,
    networkStatus: networkStatusAssessments,
  });

  const {
    columns: columnsAssessment,
    GTColumnSelectAnchorExported: GTColumnSelectAnchorExportedAssessment,
    selectedAssessment,
    setSelectedAssessment,
  } = useAssessmentColumns({
    data: assessments || [],
  });

  const { columns, GTColumnSelectAnchorExported, checked, resetChecked } = useScenarioColumns({
    data: assessments.find((el) => el.id === selectedAssessment)?.scenarios || [],
    linkedScenarios: incidentScenarios,
    incidentId,
  });

  useEffect(() => {
    if (incidentScenariosData) {
      setIncidentScenarios(
        incidentScenariosData.find((el: any) => el.id === incidentId)?.scenarios.map((el) => el.id) || [],
      );
    }
  }, [incidentScenariosData]);

  const [linkIncidentToScenarios, { loading: isLinking }] = useLinkIncidentToScenariosMutation({
    onCompleted: () => {
      queryCache.invalidateQueries();
      refetchQuery({ include: ['incident'] });
      onClose();
    },
    onError: (err: any) => setErr(err.message || 'Something went wrong'),
  });

  const isModalLoading = isAssessmentsLoading || isIncidentsLoading;

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={onClose}
      isOpen={isCloseRequested}
      isDirty={!!checked.length}
      modalComponent={
        <StyledModal onRequestClose={() => setIsCloseRequested(true)} isOpen center={false}>
          <Spacer $px={40} />

          <div className="h-padding">
            <Poppins className="m-title" px={28}>
              {selectedAssessment ? 'Select one or more scenarios to link' : 'Select Assessment'}
            </Poppins>
            {subTitle && <ModalSubTitle title={subTitle.title} value={subTitle.value} />}

            {selectedAssessment ? (
              <>
                <Spacer $px={10} />
                <div
                  className="back"
                  onClick={() => {
                    setSelectedAssessment('');
                    resetChecked();
                    mpEvent(MPEvents.BackArrowClick, {});
                  }}
                >
                  <Back />
                  <Poppins className="back__text" px={18}>
                    Back
                  </Poppins>
                </div>
                <Spacer $px={20} />
              </>
            ) : (
              <Spacer $px={20} />
            )}

            {selectedAssessment ? (
              <GenericTable
                tableId={TableIds.incidentLinkScenarioScenario}
                data={assessments.find((el) => el.id === selectedAssessment)?.scenarios || []}
                columns={columns}
                GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
                itemHeight={50}
                searchable={['name', 'source', 'event', 'consequence', 'expected_loss', 'updated_at']}
                rowDisabled={(row) => incidentScenarios.some((item) => item === row.id)}
              />
            ) : (
              <>
                {!isModalLoading ? (
                  <GenericTable
                    tableId={TableIds.incidentLinkScenarioAssessment}
                    data={assessments || []}
                    columns={columnsAssessment}
                    GTColumnSelectAnchorExported={GTColumnSelectAnchorExportedAssessment}
                    itemHeight={50}
                    rowDisabled={(row) => row.scenarios.length === 0}
                    searchable={['name', 'description']}
                  />
                ) : (
                  <TableSkeleton />
                )}
              </>
            )}
          </div>

          {selectedAssessment && (
            <>
              <Spacer $px={30} />
              <div className="h-padding">
                <Button
                  disabled={isLinking || !checked.length}
                  onClick={() => {
                    linkIncidentToScenarios({
                      variables: {
                        incidentId,
                        scenarioIds: checked,
                      },
                    });
                    mpEvent(MPEvents.ButtonClick, {
                      button: 'Confirm',
                      modal: 'Link scenarios to incident modal',
                      tags: ['INCIDENT', 'SCENARIO'],
                    });
                  }}
                  primary
                  css="width: 100%;"
                  data-cy="submit-btn"
                >
                  CONFIRM
                </Button>

                {!!err && (
                  <>
                    <Spacer $px={15} />
                    <div className="error">{err}</div>
                  </>
                )}
              </div>
            </>
          )}
          <Spacer $px={30} />
        </StyledModal>
      }
    />
  );
};
